import DirectionsWalkOutlinedIcon from "@mui/icons-material/DirectionsWalkOutlined";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setCachedAnimationDetails } from "../../../app/slices/AnimationsSlice";
import {
  selectSelectedElement,
  updateSelectedInteraction,
} from "../../../app/slices/TrainingSlice";
import PiButton from "../../../components/PiButton";
import { AnimationDetailsDTO } from "../../../dto/AnimationDetailsDTO";
import {
  getAnimateActorAnimationId,
  getAnimateActorAnimationName,
  getAnimateActorAnimationState,
  type IAnimateActor,
} from "../../../models/AnimateActor";
import AnimationLibrary from "../animation/AnimationLibrary";
import {
  ANIMATION_UNAVAILABLE_LABEL,
  getAnimationDisplayLabel,
} from "../animation/animationDisplay";
import useAnimateActorAnimationDetails from "../animation/useAnimateActorAnimationDetails.hook";
import { useAnimationRender } from "../animation/useAnimationAssets.hook";
import PiPopupWindowPreview from "../components/PiPopupWindowPreview";
import PiPopupHeaderBar from "../components/PiPopupHeaderBar";
import { InteractionType } from "../../../models/interactions/Interaction";
import MessageBubbleActorDropdown from "../message_bubble/MessageBubbleActorDropdown";

export function AnimateActorPreviewBody() {
  const interaction = useSelector(selectSelectedElement) as IAnimateActor;
  const animationName = getAnimateActorAnimationName(interaction.animation);
  const animationState = getAnimateActorAnimationState(interaction.animation);
  const animationId = getAnimateActorAnimationId(interaction.animation);
  const dispatch = useDispatch();
  const { details: animationDetails, isUnavailable } =
    useAnimateActorAnimationDetails(interaction);
  const { renderUri } = useAnimationRender(animationId || undefined);

  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [hasImageError, setHasImageError] = useState(false);

  useEffect(() => {
    setIsImageLoaded(false);
    setHasImageError(false);
  }, [renderUri]);

  const updateInteraction = (patch: Partial<IAnimateActor>) => {
    dispatch(updateSelectedInteraction({ ...interaction, ...patch }));
  };

  const onSelectAnimation = (details: AnimationDetailsDTO) => {
    updateInteraction({
      animation: {
        name: details.name,
        animationState: details.animationState,
        animationId: details.externalId,
      },
      layer: details.layer,
    });

    dispatch(
      setCachedAnimationDetails({
        externalId: details.externalId,
        animationDetails: details,
      })
    );
  };

  const animationLabel = animationName
    ? getAnimationDisplayLabel(
        animationDetails ?? {
          name: animationName,
          animationState,
        }
      )
    : "No animation selected";

  return (
    <div className="flex h-full w-full flex-col gap-[20px] overflow-y-auto pit-scrollbar pr-[4px]">
      <MessageBubbleActorDropdown
        label="Actor"
        selectedTargetActorId={interaction.npcId}
        onClick={(npcId) => updateInteraction({ npcId })}
      />

      <div className="flex min-h-0 w-full flex-1 flex-col gap-[12px]">
        <div className="text-[14px] font-bold text-interaction-primary">
          Animation
        </div>
        <div
          className="relative flex min-h-[160px] w-full flex-1 items-center justify-center overflow-hidden rounded-[5px] bg-interaction-softBackground"
          data-testid="animate-actor-animation-preview"
        >
          {renderUri && !hasImageError ? (
            <>
              {!isImageLoaded && (
                <div className="absolute inset-0 animate-pulse bg-creator-surface" />
              )}
              <img
                className="h-full w-full object-contain p-[10px]"
                src={renderUri}
                alt={animationName}
                onLoad={() => setIsImageLoaded(true)}
                onError={() => {
                  setHasImageError(true);
                  setIsImageLoaded(false);
                }}
              />
            </>
          ) : (
            <div className="flex flex-col items-center justify-center gap-[8px] text-[48px] text-creator-text-soft">
              <DirectionsWalkOutlinedIcon fontSize="inherit" color="inherit" />
              {isUnavailable && (
                <div
                  className="text-[13px] font-medium"
                  data-testid="animation-unavailable"
                >
                  {ANIMATION_UNAVAILABLE_LABEL}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex min-h-[48px] w-full items-center gap-[10px] rounded-[5px] bg-creator-surface px-[12px] py-[8px]">
          <div className="flex h-[32px] w-[32px] shrink-0 items-center justify-center text-[24px] text-interaction-primary">
            <DirectionsWalkOutlinedIcon fontSize="inherit" color="inherit" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[14px] font-medium text-interaction-primary">
              {animationLabel}
            </div>
            <div className="text-[12px] font-medium text-interaction-primary">
              Layer {interaction.layer}
            </div>
          </div>
        </div>

        <div className="h-[36px]">
          <PiButton
            variant="piBlueOutlined"
            onClick={() => setIsLibraryOpen(true)}
          >
            {animationName ? "Change Animation" : "Select Animation"}
          </PiButton>
        </div>
      </div>

      {isLibraryOpen && (
        <AnimationLibrary
          isOpen={isLibraryOpen}
          onClose={() => setIsLibraryOpen(false)}
          title="Animation Library"
          width="calc(90vw / var(--app-zoom, 1))"
          height="calc(90vh / var(--app-zoom, 1))"
          onSubmit={onSelectAnimation}
        />
      )}
    </div>
  );
}

export default function AnimateActorPreview() {
  return (
    <PiPopupWindowPreview
      size="compact"
      header={<PiPopupHeaderBar interactionType={InteractionType.AnimateActor} title="Animate Actor" />}
      body={<AnimateActorPreviewBody />}
    />
  );
}
